import { SeededRandom } from './SeededRandom';
import type { LakeFeature, Point, RiverFeature, WorldGeneratorConfig } from './types';

export class LakeGenerator {
  private readonly random: SeededRandom;

  constructor(private readonly config: WorldGeneratorConfig) {
    this.random = new SeededRandom(config.seed.seed).fork('lake');
  }

  generateLake(anchor?: Point): LakeFeature {
    const center = anchor ?? { x: Math.max(7, Math.floor(this.config.width * 0.24)), y: Math.floor(this.config.height * 0.68) };
    const tiles = anchor ? this.smoothBlob(this.blob(center, 12), center) : this.westernLake();
    const shoreline = this.findShoreline(tiles);
    const islands: Point[] = [];
    return {
      id: 'western-lake',
      kind: 'lake',
      center,
      tiles,
      shoreline,
      islands,
    };
  }

  private westernLake(): readonly Point[] {
    const offsetX = Math.max(2, Math.floor(this.config.width * 0.13) + this.random.int(-1, 1));
    const offsetY = Math.max(8, Math.floor(this.config.height * 0.62) + this.random.int(-1, 1));
    const shape: readonly Point[] = [
      { x: offsetX + 2, y: offsetY },
      { x: offsetX + 3, y: offsetY },
      { x: offsetX + 4, y: offsetY },
      { x: offsetX + 1, y: offsetY + 1 },
      { x: offsetX + 2, y: offsetY + 1 },
      { x: offsetX + 3, y: offsetY + 1 },
      { x: offsetX + 4, y: offsetY + 1 },
      { x: offsetX + 5, y: offsetY + 1 },
      { x: offsetX, y: offsetY + 2 },
      { x: offsetX + 1, y: offsetY + 2 },
      { x: offsetX + 2, y: offsetY + 2 },
      { x: offsetX + 3, y: offsetY + 2 },
      { x: offsetX + 4, y: offsetY + 2 },
      { x: offsetX + 5, y: offsetY + 2 },
      { x: offsetX + 6, y: offsetY + 2 },
      { x: offsetX + 1, y: offsetY + 3 },
      { x: offsetX + 2, y: offsetY + 3 },
      { x: offsetX + 3, y: offsetY + 3 },
      { x: offsetX + 4, y: offsetY + 3 },
      { x: offsetX + 5, y: offsetY + 3 },
      { x: offsetX + 6, y: offsetY + 3 },
      { x: offsetX + 2, y: offsetY + 4 },
      { x: offsetX + 3, y: offsetY + 4 },
      { x: offsetX + 4, y: offsetY + 4 },
      { x: offsetX + 5, y: offsetY + 4 },
      { x: offsetX + 3, y: offsetY + 5 },
      { x: offsetX + 4, y: offsetY + 5 },
    ];
    return shape.filter((point) => this.isInside(point));
  }

  generateRiverMouthLake(river: RiverFeature): LakeFeature | undefined {
    void river;
    return undefined;
    /*
    if (river.mouth.y < this.config.height - 1) return undefined;
    return this.generateLake({ x: river.mouth.x, y: Math.max(2, river.mouth.y - 2) });
    */
  }

  private blob(center: Point, targetSize: number): readonly Point[] {
    const tiles = new Map<string, Point>();
    const radiusX = targetSize > 14 ? 3.2 : 2.6;
    const radiusY = targetSize > 14 ? 2.2 : 2;

    for (let y = Math.floor(center.y - radiusY - 1); y <= Math.ceil(center.y + radiusY + 1); y++) {
      for (let x = Math.floor(center.x - radiusX - 1); x <= Math.ceil(center.x + radiusX + 1); x++) {
        const point = { x, y };
        if (!this.isInside(point)) continue;
        const nx = (x - center.x) / radiusX;
        const ny = (y - center.y) / radiusY;
        const distance = nx * nx + ny * ny;
        const edgeNoise = this.random.float(-0.08, 0.12);
        if (distance <= 0.78 || distance <= 1.02 + edgeNoise) tiles.set(key(point), point);
      }
    }

    for (const point of neighbors(center)) {
      if (this.isInside(point)) tiles.set(key(point), point);
    }
    tiles.set(key(center), center);

    return [...tiles.values()];
  }

  private smoothBlob(points: readonly Point[], center: Point): readonly Point[] {
    const water = new Map(points.map((point) => [key(point), point]));
    const candidates = new Map<string, Point>();
    for (const point of points) {
      for (const neighbor of neighbors(point)) {
        if (!this.isInside(neighbor) || water.has(key(neighbor))) continue;
        candidates.set(key(neighbor), neighbor);
      }
    }

    for (const candidate of candidates.values()) {
      const waterNeighbors = neighbors(candidate).filter((neighbor) => water.has(key(neighbor))).length;
      const nearCenter = Math.hypot(candidate.x - center.x, candidate.y - center.y) <= 4.2;
      if (waterNeighbors >= 3 || (nearCenter && waterNeighbors >= 2)) water.set(key(candidate), candidate);
    }

    for (const point of [...water.values()]) {
      const waterNeighbors = neighbors(point).filter((neighbor) => water.has(key(neighbor))).length;
      if (waterNeighbors <= 1 && Math.hypot(point.x - center.x, point.y - center.y) > 2.5) water.delete(key(point));
    }

    return [...water.values()];
  }

  private findShoreline(waterTiles: readonly Point[]): readonly Point[] {
    const water = new Set(waterTiles.map(key));
    const shoreline = new Map<string, Point>();
    for (const tile of waterTiles) {
      for (const neighbor of neighbors(tile)) {
        if (!this.isInside(neighbor) || water.has(key(neighbor))) continue;
        shoreline.set(key(neighbor), neighbor);
      }
    }
    return [...shoreline.values()];
  }

  private isInside(point: Point): boolean {
    return point.x >= 0 && point.y >= 0 && point.x < this.config.width && point.y < this.config.height;
  }
}

function neighbors(point: Point): readonly Point[] {
  return [
    { x: point.x + 1, y: point.y },
    { x: point.x - 1, y: point.y },
    { x: point.x, y: point.y + 1 },
    { x: point.x, y: point.y - 1 },
  ];
}

function key(point: Point): string {
  return `${point.x}:${point.y}`;
}
